import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  TagIcon, 
  ArrowLeftIcon,
  DocumentTextIcon,
  StarIcon,
  LinkIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { RepresentativePaper } from '../types';

const RepresentativePaperDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [paper, setPaper] = useState<RepresentativePaper | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPaperDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/representative-papers/${id}`);
      const result = await response.json();
      
      if (result.success) {
        setPaper(result.data);
      } else {
        setError(result.message || '获取论文详情失败');
      }
    } catch (error) {
      console.error('获取论文详情失败:', error);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPaperDetail();
    }
  }, [id, fetchPaperDetail]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !paper) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">论文不存在</h2>
          <p className="text-gray-600 mb-6">{error || '该论文可能已被删除或移动'}</p>
          <button
            onClick={() => navigate('/research')}
            className="btn btn-primary"
          >
            返回研究方向
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* 返回按钮 */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/research')}
          className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          返回研究方向
        </button>
      </div>

      {/* 论文头部 */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
          <span className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-1" />
            {paper.year}年
          </span>
          <span className="flex items-center">
            <TagIcon className="w-4 h-4 mr-1" />
            {paper.journal}
          </span>
          {paper.isHighlighted && (
            <span className="flex items-center text-yellow-600">
              <StarIcon className="w-4 h-4 mr-1" />
              高亮论文
            </span>
          )}
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">{paper.title}</h1>
        
        {/* 作者信息 */}
        {paper.authors && paper.authors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">作者</h3>
            <div className="flex flex-wrap gap-3">
              {paper.authors.map((author, index) => (
                <div key={index} className="bg-gray-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium">{author}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 关键词 */}
        {paper.keywords && paper.keywords.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">关键词</h3>
            <div className="flex flex-wrap gap-2">
              {paper.keywords.map((keyword, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 封面图片 */}
      {paper.coverImage && (
        <div className="mb-8">
          <img 
            src={paper.coverImage} 
            alt={paper.title}
            className="w-full h-64 object-cover rounded-lg shadow-lg"
          />
        </div>
      )}

      {/* 摘要 */}
      {paper.abstract && (
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">摘要</h3>
          <div className="prose max-w-none text-gray-700 leading-relaxed">
            <p className="whitespace-pre-wrap">{paper.abstract}</p>
          </div>
        </div>
      )}

      {/* 外部链接 */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">相关链接</h3>
        <div className="space-y-3">
          {paper.doi && (
            <div className="flex items-center space-x-3">
              <LinkIcon className="w-4 h-4 text-gray-500" />
              <a
                href={`https://doi.org/${paper.doi}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                DOI: {paper.doi}
              </a>
            </div>
          )}
          {paper.pdfUrl && (
            <div className="flex items-center space-x-3">
              <DocumentArrowDownIcon className="w-4 h-4 text-gray-500" />
              <a
                href={paper.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                下载PDF
              </a>
            </div>
          )}
        </div>
      </div>

      {/* 论文信息 */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">论文信息</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">期刊：</span>
            <span className="text-gray-600">{paper.journal}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">年份：</span>
            <span className="text-gray-600">{paper.year}</span>
          </div>
          {paper.doi && (
            <div>
              <span className="font-medium text-gray-700">DOI：</span>
              <span className="text-gray-600">{paper.doi}</span>
            </div>
          )}
          <div>
            <span className="font-medium text-gray-700">状态：</span>
            <span className={`px-2 py-1 rounded text-xs ${
              paper.isHighlighted 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {paper.isHighlighted ? '高亮论文' : '普通论文'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepresentativePaperDetail;




